import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from 'react';
import cn from 'classnames';
import { useSelector, useDispatch } from 'react-redux';
import { useQueryClient } from 'react-query';
import moment from 'moment';
import { Spin } from 'antd';
import {
  CalendarLocalProvider,
  CalendarReduxProvider,
  useCalendarDispatch,
  useCalendarState,
} from './context/CalendarContext';
import Header from './components/CalendarHeader/CalendarHeader';
import CurrentDate from './components/CalendarCurrentDate/CalendarCurrentDate';
import CalendarWorkers from './components/CalendarWorkers/CalendarWorkers';
import Grid from './components/CalendarGrid/CalendarGrid';
import RequestCardDrawer from '../../components/RequestCardDrawer/RequestCardDrawer';
import NewDocumentContainer from '../new-wdocument.container';
import WarrantyInfoContainer from '../warranty-info-form.container';
import WarrantyContainer from '../warranty-form.container';
import ExecuteTimeContainer from '../set-execute-time-panel.container';
import ExecuterContainer from '../set-executer-panel.container';
import MasterContainer from '../set-master-panel.container';
import ServiceContainer from '../set-service-panel.container';
import AddressContainer from '../set-address-panel.container';
import useTasks from './queries/useTasks';
import { fromDateStr, toDateStr } from '../../helpers/utcDate';
import {
  setCalendarData,
  setWorkers,
  setCalendarSheet,
  setView,
  setMode,
  setCalendarState,
  setLocalTask,
  setInfoModalVisible,
  setChangeableTaskStatus,
  setInnerActivationOfChange,
} from './store/actions/calendarActions';
import {
  setRequestDrawerVisible,
  setSelectedRequestId,
} from '../../actions/requestCardActions';
import { getWorkersAndCalendarData } from './utils/getWorkersAndCalendarData';
import { views } from './constants/views';
import { modes } from './constants/modes';
import { states } from './constants/states';
import useRequestCardMutationFns from '../../hooks/useRequestCardMutationFns';
import useTaskDataFromParams from './hooks/useTaskDataFromParams';
import {
  getEndPeriod,
  getStartPeriod,
  getTotalTime,
} from './utils/mainPageHelpers';
import useRequest from '../../queries/useRequest';
import InfoModal from './components/InfoModal/InfoModal';
import {
  SHOW_NO_MORE_INFO_MODAL,
  changeableTaskStatuses,
} from './constants/common';
import { generateLocalTask } from './utils/generateLocalTask';
import { setItemToCalendar } from '../../actions/itemToCalendar';
import { getQueryKeys } from './queries/queryKeys';
import useSetRefreshFn from '../../hooks/useSetRefreshFn';
import useResetPageState from '../../crossPageInteractions/hooks/useResetPageState';
import useDeleteTaskMutation from './mutations/useDeleteTaskMutation';
import useSuccessTaskMutation from './hooks/useSuccessTaskMutation';
import styles from './Calendar.module.scss';

const offsetTop = 102;
const windowHeightDelta = 288;
const workersWrapStyle = {
  overflowY: 'auto',
  position: 'sticky',
};

const showModal = () => {
  let show;

  try {
    show = localStorage.getItem(SHOW_NO_MORE_INFO_MODAL);
  } catch (error) {
    show = null;
  }

  return !show;
};

const ExternalCalendar = ({
  initialSheet,
  initialView,
  initialMode,
  initialState,
  calendarData,
  schedules,
  getCalendarData,
  totalTime: totalTimeProp,
  className,
  headerProps,
  resourceRows,
  resourceColumnTitle,
  resourceColumnWidth,
  resourceDateColumnMinWidth,
  resourceDateColumnMaxWidth,
  resourceHeaderHeight,
  resourceRowMinHeight,
  resourceCompact = false,
  renderResourceRowHeader,
  renderResourceFooter,
  renderResourceTask,
  renderResourceEmptyTask,
  resourceEmptyText,
  showCurrentDate = true,
}) => {
  const dispatch = useCalendarDispatch();
  const { view, mode, calendarSheet } = useCalendarState();

  useEffect(() => {
    dispatch(setCalendarSheet(initialSheet || moment()));
    dispatch(setView(initialView || views.month));
    dispatch(setMode(initialMode || modes.calendar));
    dispatch(setCalendarState(initialState || states.viewTasks));
  }, [dispatch, initialSheet, initialView, initialMode, initialState]);

  const startPeriod = useMemo(
    () => getStartPeriod(view, calendarSheet, mode),
    [calendarSheet, view, mode]
  );

  const endPeriod = useMemo(
    () => getEndPeriod(view, calendarSheet, mode),
    [calendarSheet, view, mode]
  );

  const calendarDataFromSchedules = useMemo(() => {
    if (
      !view ||
      !calendarSheet ||
      !mode ||
      !Array.isArray(schedules) ||
      typeof getCalendarData !== 'function'
    ) {
      return null;
    }

    return getCalendarData(schedules, view, calendarSheet, mode);
  }, [schedules, getCalendarData, view, calendarSheet, mode]);

  useEffect(() => {
    const dataToSet = calendarData || calendarDataFromSchedules;

    if (Array.isArray(dataToSet)) {
      dispatch(setCalendarData(dataToSet));
    }
  }, [dispatch, calendarData, calendarDataFromSchedules]);

  const totalTime = useMemo(
    () => totalTimeProp ?? getTotalTime(view, startPeriod, endPeriod),
    [totalTimeProp, view, startPeriod, endPeriod]
  );

  return (
    <div className={cn(styles.embeddedRoot, className)}>
      <div className={styles.embeddedTopAffixContainer}>
        <Header {...headerProps} />
        {showCurrentDate && <CurrentDate />}
      </div>
      <Grid
        totalTime={totalTime}
        rootClassName={styles.embeddedGrid}
        resourceRows={resourceRows}
        resourceColumnTitle={resourceColumnTitle}
        resourceColumnWidth={resourceColumnWidth}
        resourceDateColumnMinWidth={resourceDateColumnMinWidth}
        resourceDateColumnMaxWidth={resourceDateColumnMaxWidth}
        resourceHeaderHeight={resourceHeaderHeight}
        resourceRowMinHeight={resourceRowMinHeight}
        resourceCompact={resourceCompact}
        renderResourceRowHeader={renderResourceRowHeader}
        renderResourceFooter={renderResourceFooter}
        renderResourceTask={renderResourceTask}
        renderResourceEmptyTask={renderResourceEmptyTask}
        resourceEmptyText={resourceEmptyText}
      />
    </div>
  );
};

const CalendarPage = ({ showCurrentDate = true }) => {
  const { calendarSheet, mode, view, state, innerActivationOfChange, workers } =
    useSelector((state) => state.newCalendarStore);

  const itemToCalendar = useSelector((state) => state.itemToCalendarStore.item);

  const dispatch = useDispatch();

  const queryClient = useQueryClient();

  const { requestId } = useTaskDataFromParams();

  const request = useRequest(requestId, {
    enabled:
      state !== states.viewTasks
        ? Boolean(requestId) && !itemToCalendar
        : false,
  });

  const [workersHeight, setWorkersHeight] = useState(0);
  const [showAllWorkers, setShowAllWorkers] = useState(false);

  const workersRef = useRef();

  const clientHeight = document.documentElement.clientHeight;

  const startPeriod = useMemo(
    () => getStartPeriod(view, calendarSheet, mode),
    [calendarSheet, view, mode]
  );

  const endPeriod = useMemo(
    () => getEndPeriod(view, calendarSheet, mode),
    [calendarSheet, view, mode]
  );

  const totalTime = useMemo(
    () => getTotalTime(view, startPeriod, endPeriod),
    [view, startPeriod, endPeriod]
  );

  useResetPageState({ page: 'Requests' });

  const {
    handleMasterChange,
    handleExecuterChange,
    handleAddressChange,
    handleServiceChange,
    handleExecutionTimeChange,
    handleWarrantyInfoChange,
    handleWarrantyDocsAdd,
  } = useRequestCardMutationFns();

  const handleShowAllWorkersClick = useCallback(
    () => setShowAllWorkers((prev) => !prev),
    []
  );

  const handleRequestCardDrawerClose = useCallback(() => {
    if (innerActivationOfChange) {
      dispatch(setInnerActivationOfChange(false));
    }
  }, [dispatch, innerActivationOfChange]);

  const handleRequestCardCalendarClick = useCallback(() => {
    dispatch(setInnerActivationOfChange(true));
  }, [dispatch]);

  const handleDeleteSuccess = useSuccessTaskMutation(
    'Заявка удалена из календаря'
  );
  const resetTaskState = useSuccessTaskMutation();
  const { mutate: deleteTask } = useDeleteTaskMutation({
    onSuccess: handleDeleteSuccess,
  });

  const handleTaskOpen = useCallback(
    (task) => {
      dispatch(setSelectedRequestId(task.requestId));
      dispatch(setRequestDrawerVisible(true));
    },
    [dispatch]
  );

  const handleTaskEditCancel = useCallback(() => {
    if (!moment(itemToCalendar.taskStart).isSame(calendarSheet, view)) {
      dispatch(setCalendarSheet(moment(itemToCalendar.taskStart)));
    }

    const task = generateLocalTask(
      itemToCalendar,
      moment(itemToCalendar.taskStart),
      moment(itemToCalendar.taskEnd)
    );

    dispatch(setLocalTask(task));
    dispatch(setChangeableTaskStatus(changeableTaskStatuses.choice));
  }, [calendarSheet, dispatch, itemToCalendar, view]);

  const handleTaskDelete = useCallback(
    (task) => deleteTask({ taskId: task.taskId }),
    [deleteTask]
  );

  const handleTaskEditExit = useCallback(() => {
    resetTaskState();
  }, [resetTaskState]);

  const requestInteractions = useMemo(
    () => ({
      onTaskDelete: handleTaskDelete,
      onTaskEditCancel: handleTaskEditCancel,
      onTaskEditExit: handleTaskEditExit,
      onTaskOpen: handleTaskOpen,
    }),
    [handleTaskDelete, handleTaskEditCancel, handleTaskEditExit, handleTaskOpen]
  );

  const { data: tasks, isFetching: isTasksFetching } = useTasks(
    {
      fromDate: fromDateStr(startPeriod),
      toDate: toDateStr(endPeriod),
      workerId:
        state !== states.viewTasks &&
        !showAllWorkers &&
        Boolean(itemToCalendar?.executer?.id)
          ? itemToCalendar.executer.id
          : undefined,
    },
    {
      enabled:
        Boolean(startPeriod) &&
        Boolean(endPeriod) &&
        (state === states.viewTasks ? true : Boolean(itemToCalendar)),
    }
  );

  useSetRefreshFn(() => {
    const keys = [
      getQueryKeys.tasks({
        fromDate: fromDateStr(startPeriod),
        toDate: toDateStr(endPeriod),
        workerId:
          state !== states.viewTasks &&
          !showAllWorkers &&
          Boolean(itemToCalendar?.executer?.id)
            ? itemToCalendar.executer.id
            : undefined,
      }),
      getQueryKeys.request(requestId),
    ];

    keys.forEach((key) => {
      queryClient.invalidateQueries(key);
    });
  }, [
    startPeriod,
    endPeriod,
    state,
    showAllWorkers,
    itemToCalendar,
    requestId,
  ]);

  const handleAfterAddToAcceptanceCalendar = () => {
    queryClient.invalidateQueries(
      getQueryKeys.tasks({
        fromDate: fromDateStr(startPeriod),
        toDate: toDateStr(endPeriod),
        workerId:
          state !== states.viewTasks &&
          !showAllWorkers &&
          Boolean(itemToCalendar?.executer?.id)
            ? itemToCalendar.executer.id
            : undefined,
      })
    );
  };

  // set workers and calendar data
  useEffect(() => {
    if (tasks) {
      const { workers, calendarGridData } = getWorkersAndCalendarData({
        data: tasks,
        startPeriod,
        totalTime,
        view,
        taskId: itemToCalendar?.taskId,
      });

      dispatch(setWorkers(workers));
      dispatch(setCalendarData(calendarGridData));
    }
  }, [dispatch, tasks]);

  // set item to calendar
  useEffect(() => {
    if (request?.data?.length) {
      dispatch(setItemToCalendar(request.data[0]));

      return () => {
        request.remove();
      };
    }
  }, [dispatch, request]);

  //set calendar state, sheet and view and info modal visibility
  useEffect(() => {
    let newState = states.viewTasks;
    let date = calendarSheet || moment();
    let newView = view || views.month;
    let showInfoModal = false;

    if (itemToCalendar) {
      newState = itemToCalendar.taskId ? states.editTask : states.addTask;
      newView = views.week;

      showInfoModal = itemToCalendar.taskId ? false : showModal();

      if (itemToCalendar.taskId) {
        const task = generateLocalTask(
          itemToCalendar,
          moment(itemToCalendar.taskStart),
          moment(itemToCalendar.taskEnd)
        );

        dispatch(setLocalTask(task));
        dispatch(setChangeableTaskStatus(changeableTaskStatuses.choice));
      }

      if (itemToCalendar.taskStart) {
        date = moment(itemToCalendar.taskStart);
      }
    } else {
      setShowAllWorkers(false);
    }

    dispatch(setInfoModalVisible(showInfoModal));
    dispatch(setCalendarState(newState));
    dispatch(setView(newView));
    dispatch(setCalendarSheet(date));
  }, [itemToCalendar, dispatch]);

  //reset store when component unmount
  useEffect(() => {
    return () => {
      dispatch(setCalendarData([]));
      dispatch(setCalendarSheet(undefined));
      dispatch(setView(undefined));
      dispatch(setSelectedRequestId(null));
      dispatch(setRequestDrawerVisible(false));
      dispatch(setMode(modes.calendar));
      dispatch(setCalendarState(undefined));
      dispatch(setItemToCalendar(null));
      dispatch(setLocalTask(null));
      dispatch(setChangeableTaskStatus(changeableTaskStatuses.edit));
      dispatch(setInnerActivationOfChange(false));
    };
  }, []);

  // set workers container height
  useEffect(() => {
    if (workersRef.current) {
      setWorkersHeight(workersRef.current.offsetHeight);
    }
  }, [workers]);

  useEffect(() => {
    const handleBeforePrint = () => {
      window.scrollTo(0, 0);
    };

    window.addEventListener('beforeprint', handleBeforePrint);
    return () => window.removeEventListener('beforeprint', handleBeforePrint);
  }, []);

  return (
    <>
      <CalendarReduxProvider requestInteractions={requestInteractions}>
        <div className={styles.root}>
          <Spin
            spinning={isTasksFetching}
            wrapperClassName={styles.spinWrapper}
          >
            <div className={styles.workersContainer}>
              <div style={{ height: `${offsetTop}px`, flexShrink: 0 }} />
              <div
                className={styles.scrollBar}
                style={{
                  ...workersWrapStyle,
                  top: `${offsetTop}px`,
                  height:
                    clientHeight - windowHeightDelta > workersHeight
                      ? `${workersHeight}px`
                      : `calc(100vh - ${offsetTop}px)`,
                }}
              >
                <CalendarWorkers
                  ref={workersRef}
                  showAllWorkers={showAllWorkers}
                  onShowAllWorkersClick={handleShowAllWorkersClick}
                />
              </div>
            </div>
            <div className={styles.topAffixContainer}>
              <Header />
              {showCurrentDate && <CurrentDate />}
            </div>
            <Grid totalTime={totalTime} rootClassName={styles.grid} />
          </Spin>
          <RequestCardDrawer
            calendarIcon={state === states.viewTasks}
            onClose={handleRequestCardDrawerClose}
            onCalendarClick={handleRequestCardCalendarClick}
            afterAddToAcceptanceCalendar={handleAfterAddToAcceptanceCalendar}
          />
          <WarrantyContainer />
          <NewDocumentContainer onAddDocument={handleWarrantyDocsAdd} />
          <WarrantyInfoContainer
            onWarrantyInfoChange={handleWarrantyInfoChange}
          />
          <ExecuteTimeContainer updateExecuteTime={handleExecutionTimeChange} />
          <ExecuterContainer updateExecuter={handleExecuterChange} />
          <MasterContainer updateMaster={handleMasterChange} />
          <ServiceContainer updateService={handleServiceChange} />
          <AddressContainer updateAddress={handleAddressChange} />
          <InfoModal />
        </div>
      </CalendarReduxProvider>
    </>
  );
};

const Calendar = ({ external = false, requestInteractions, ...props }) => {
  if (external) {
    return (
      <CalendarLocalProvider requestInteractions={requestInteractions}>
        <ExternalCalendar {...props} />
      </CalendarLocalProvider>
    );
  }

  return <CalendarPage {...props} />;
};

export default Calendar;
